import { Organization, User } from '@prisma/client'
import { useNavigate, useParams } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { Api } from '~/core/trpc'
import { OrganizationType } from '~/features/organization/common'
import { Utility } from '~/plugins/toolbox/shared/utility'

export const useOrganization = (options: {
  organizations: Organization[]
  user: User
  isLoggedIn: boolean
  isAllowed: boolean
}) => {
  const navigate = useNavigate()
  const { organizations, user, isLoggedIn, isAllowed } = options

  const [organizationIdFirst, setOrganizationIdFirst] = useState<string>()
  const organizationIdSaved = user
    ? localStorage.getItem(`organization-${user?.id}`)
    : null

  const params = useParams<{ organizationId: string }>()

  const organizationId =
    params.organizationId ??
    organizationIdSaved ??
    organizationIdFirst ??
    organizations[0]?.id

  const canFetch = isLoggedIn && !!organizationId && isAllowed

  const queryOrganization = Api.organization.findUnique.useQuery(
    {
      where: { id: organizationId, members: { some: { userId: user?.id } } },
      include: { company: true, members: { where: { userId: user?.id } } },
    },
    {
      enabled: canFetch,
      onSuccess(data) {
        if (data?.id) {
          localStorage.setItem(`organization-${user?.id}`, data?.id)
        } else {
          localStorage.removeItem(`organization-${user?.id}`)

          navigate('/organizations', { replace: true })
        }
      },
      onError(error) {
        console.log(error)
        // localStorage.deleteItem(`organization-${user?.id}`)
        // navigate('/organizations', { replace: true })
      },
    },
  )

  const handleRefreshOrganization = async () => {
    const canFetch =
      Utility.isDefined(organizationId) && Utility.isDefined(options.user)

    if (canFetch) {
      await queryOrganization.refetch().catch(() => {})
    }
  }

  const isNotFound = queryOrganization.isFetched && !queryOrganization.data
  const isNoneFound = isLoggedIn && (!organizationId || isNotFound)

  useEffect(() => {
    if (!isAllowed) {
      return
    }

    if (!isLoggedIn) {
      setOrganizationIdFirst(null)
      queryOrganization.remove()
    }
  }, [isLoggedIn, isAllowed])

  useEffect(() => {
    if (!isAllowed) {
      return
    }

    if (isNoneFound && organizations.length > 0) {
      setOrganizationIdFirst(organizations[0]?.id)
    }
  }, [isNoneFound, organizations, isAllowed])

  const isReady =
    isAllowed && (queryOrganization.isFetched || organizations.length === 0)

  const {
    members = [],
    company,
    ...organization
  } = queryOrganization.data ?? {}

  const organizationMember = members[0]

  const notificationCounters =
    organizationMember?.notificationCounters as Record<string, number>

  return {
    ...queryOrganization,
    data: organization?.['id'] ? (organization as OrganizationType) : null,
    company,
    notificationCounters,
    organizationMember,
    isReady,
  }
}
